import { Injectable } from '@angular/core';

import {
  AddCartItemRequest,
  Cart,
  GetCartInfoRequest,
  GetShippingInfosRequest,
  GetShippingInfosResponse,
  MergeCartRequest,
  UpdateCartRequest,
  VerifyReceiptBarcodeRequest,
} from '@proto/store/api/store/cart';
import { LativHttpClient } from '@service/lativ.interceptor';
import { AuthService } from '@service/auth.service';
import { CreateOrderRequest, CreateOrderResponse } from '@proto/store/api/store/orders/orders';
import { ReceiverPhone, SafeStorageService } from '@service/safe-storage.service';
import { Recipient_Type } from '@proto/api/common/common';
import { PreferenceKey } from '@helper/preference-key';

import { map, Observable, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CartRepositoryService {
  constructor(
    private http: LativHttpClient,
    private authService: AuthService,
    private localService: SafeStorageService
  ) {}

  getCart(): Observable<Cart> {
    return this.authService
      .refreshTokenIfNeeded()
      .pipe(switchMap(() => this.http.get('v1/cart').pipe(map(res => Cart.fromJSON(res)))));
  }

  getCartInfo(req: GetCartInfoRequest): Observable<Cart> {
    return this.http.post('v1/cart:getInfo', GetCartInfoRequest.toJSON(req)).pipe(map(res => Cart.fromJSON(res)));
  }

  addCartItem(req: AddCartItemRequest): Observable<Cart> {
    return this.authService
      .refreshTokenIfNeeded()
      .pipe(
        switchMap(() =>
          this.http.post('v1/cart:add', AddCartItemRequest.toJSON(req)).pipe(map(res => Cart.fromJSON(res)))
        )
      );
  }

  getShippingInfos(req: GetShippingInfosRequest): Observable<GetShippingInfosResponse> {
    return this.authService.refreshTokenIfNeeded().pipe(
      switchMap(() =>
        this.http
          .post('v1/cart:shippingInfos', GetShippingInfosRequest.toJSON(req))
          .pipe(map(res => GetShippingInfosResponse.fromJSON(res)))
      ),
      map(res => {
        const phones = this.localService.getPhones();
        if (phones) {
          res.recipientInfo?.recipientInfos.forEach(i => {
            const phone = phones.find(p => p.addressId === i.id)?.phone ?? '';
            if (i.recipient?.address) {
              i.recipient.address.phone = phone;
            }
            if (i.recipient?.storeAddress) {
              i.recipient.storeAddress.phone = phone;
            }
            if (i.recipient?.overseaAddress) {
              i.recipient.overseaAddress.phone = phone;
            }
          });
        }
        return res;
      })
    );
  }

  updateCart(req: UpdateCartRequest): Observable<boolean> {
    return this.authService
      .refreshTokenIfNeeded()
      .pipe(switchMap(() => this.http.put(UpdateCartRequest.toJSON(req), 'v1/cart').pipe(map(() => true))));
  }

  mergeCart(req: MergeCartRequest): Observable<Cart> {
    return this.authService
      .refreshTokenIfNeeded()
      .pipe(
        switchMap(() =>
          this.http.post('v1/cart:merge', MergeCartRequest.toJSON(req)).pipe(map(res => Cart.fromJSON(res)))
        )
      );
  }

  verifyReceiptBarcode(req: VerifyReceiptBarcodeRequest): Observable<unknown> {
    return this.authService
      .refreshTokenIfNeeded()
      .pipe(switchMap(() => this.http.post('v1/cart:verifyReceiptBarcode', VerifyReceiptBarcodeRequest.fromJSON(req))));
  }

  // 成立訂單. 信用卡/海外訂單需要3D驗證或LINE PAY訂單則需要另外call CreatePayment
  createOrder(req: CreateOrderRequest, addressId: string): Observable<CreateOrderResponse> {
    return this.authService.refreshTokenIfNeeded().pipe(
      switchMap(() => this.http.put(CreateOrderRequest.toJSON(req), 'v1/orders')),
      map(res => CreateOrderResponse.fromJSON(res)),
      tap(res => {
        if (addressId) {
          this.cachePhone(req, addressId);
          this.cachePhone(req, res.addressId);
        }
        try {
          localStorage?.removeItem(PreferenceKey.AD_TRACKING_ID);
          localStorage?.removeItem(PreferenceKey.AD_TRACKING_TIME);
        } catch {
          // ignore
        }
      })
    );
  }

  private cachePhone(req: CreateOrderRequest, addressId: string): void {
    if (!addressId) {
      return;
    }
    const receiverPhone = new ReceiverPhone(Recipient_Type.TYPE_UNSPECIFIED, addressId, '');
    if (req.recipient?.address) {
      receiverPhone.type = Recipient_Type.TYPE_TW_DELIVERY;
      receiverPhone.phone = req.recipient.address.phone;
    } else if (req.recipient?.storeAddress) {
      receiverPhone.type = Recipient_Type.TYPE_PICKUP_STORE;
      receiverPhone.phone = req.recipient.storeAddress.phone;
    } else if (req.recipient?.overseaAddress) {
      receiverPhone.type = Recipient_Type.TYPE_OVERSEA;
      receiverPhone.phone = req.recipient.overseaAddress.phone;
    }
    if (receiverPhone.phone) {
      this.localService.setPhone(receiverPhone);
    }
  }
}
